import { ApiError, createWmsApiClient, unwrap } from '@mh-wms/api-client';
import { clearToken, getToken } from '@/shared/auth/token';

/** Vite's development proxy permits the same-origin empty default. */
export const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';

function handleUnauthorized(): void {
  clearToken();
  if (!window.location.pathname.startsWith('/login')) window.location.assign('/login');
}

const transport = createWmsApiClient({
  baseUrl,
  getAccessToken: getToken,
  onUnauthorized: handleUnauthorized,
});

export const api = transport.api;
export const authedFetch = transport.authedFetch;
export { unwrap };

/** Browser-owned binary download adapter. */
export async function downloadFile(
  path: string,
  body: unknown,
  fallbackName: string,
): Promise<void> {
  const response = await authedFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let detail: unknown;
    try {
      detail = await response.json();
    } catch {
      detail = await response.text().catch(() => undefined);
    }
    throw ApiError.from(response, detail);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filenameFrom(response) ?? fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function filenameFrom(response: Response): string | undefined {
  const header = response.headers.get('Content-Disposition');
  if (!header) return undefined;
  return /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header)?.[1];
}
